import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import type { AutoBattleMode } from "../constants/autoBattle";
import { AUTO_BATTLE_MODES } from "../constants/autoBattle";
import {
  isAutoBattleModeAllowed,
  resolveAutoBattleEntitlements,
} from "../utils/autoBattle";
import type { Hero } from "../types";
import { ModalBackdrop } from "./ModalBackdrop";

interface AutoBattleModeModalProps {
  title?: string;
  selected: AutoBattleMode;
  onSelect: (mode: AutoBattleMode) => void;
  onClose: () => void;
  hero?: Hero | null;
  modeEntitlements?: { tactician: boolean; strategist: boolean };
  onNeedPremium?: () => void;
}

export function AutoBattleModeModal({
  title = "Auto-battle mode",
  selected,
  onSelect,
  onClose,
  hero,
  modeEntitlements: modeEntitlementsProp,
  onNeedPremium,
}: AutoBattleModeModalProps) {
  const modeEntitlements = modeEntitlementsProp ?? resolveAutoBattleEntitlements(hero);
  return createPortal(
    <ModalBackdrop onClose={onClose} className="z-[100] items-end bg-black/70">
      <div className="w-full max-w-sm max-h-[85vh] overflow-y-auto bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-bold text-white">{title}</div>
            <p className="text-xs text-gray-400 mt-1">Choose how your hero uses skills in combat.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 text-xl">
            ✕
          </button>
        </div>

        <div className="space-y-2">
          {AUTO_BATTLE_MODES.map((mode) => {
            const active = selected === mode.id;
            const allowed = isAutoBattleModeAllowed(mode.id, modeEntitlements);
            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => {
                  if (!allowed) {
                    onNeedPremium?.();
                    return;
                  }
                  onSelect(mode.id);
                }}
                className={clsx(
                  "w-full text-left rounded-xl border p-3 transition-colors",
                  !allowed && "opacity-50 cursor-not-allowed",
                  active
                    ? "border-kingdom-glow bg-kingdom-glow/10"
                    : "border-kingdom-light/40 bg-kingdom-dark/50 hover:border-kingdom-glow/50",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white text-sm">{mode.name}</span>
                  {mode.badge && (
                    <span
                      className={clsx(
                        "text-[10px] font-bold px-1.5 py-0.5 rounded",
                        mode.badge === "VIP"
                          ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                          : "bg-violet-500/20 text-violet-200 border border-violet-500/40",
                      )}
                    >
                      {mode.badge}
                    </span>
                  )}
                  {active && <span className="ml-auto text-emerald-400 text-sm">✓</span>}
                </div>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed">{mode.description}</p>
              </button>
            );
          })}
        </div>
      </div>
    </ModalBackdrop>,
    document.body,
  );
}

export interface AutoBattlePrefsPayload {
  mode: AutoBattleMode;
  prompt_on_start: boolean;
  tactical_skills: string[] | null;
}

interface AutoBattleSettingsPanelProps {
  mode: AutoBattleMode;
  promptOnStart: boolean;
  tacticalSkills: string[] | null;
  learnedSkills: string[];
  skillLabel: (skill: string) => string;
  modeEntitlements?: { tactician: boolean; strategist: boolean };
  onNeedPremium?: () => void;
  onChange: (prefs: AutoBattlePrefsPayload) => void;
}

export function AutoBattleSettingsPanel({
  mode,
  promptOnStart,
  tacticalSkills,
  learnedSkills,
  skillLabel,
  modeEntitlements,
  onNeedPremium,
  onChange,
}: AutoBattleSettingsPanelProps) {
  const limitSkills = tacticalSkills != null && tacticalSkills.length > 0;
  const [pickSkills, setPickSkills] = useState(limitSkills);
  const [selectedSkills, setSelectedSkills] = useState<string[]>(
    tacticalSkills?.length ? tacticalSkills : learnedSkills,
  );

  useEffect(() => {
    setPickSkills(tacticalSkills != null && tacticalSkills.length > 0);
    setSelectedSkills(tacticalSkills?.length ? tacticalSkills : learnedSkills);
  }, [tacticalSkills, learnedSkills]);

  const emit = (
    nextMode: AutoBattleMode,
    nextPrompt: boolean,
    nextPickSkills: boolean,
    nextSkills: string[],
  ) => {
    const tacticalMode = nextMode !== "rotation";
    onChange({
      mode: nextMode,
      prompt_on_start: nextPrompt,
      tactical_skills: tacticalMode && nextPickSkills ? nextSkills : null,
    });
  };

  const toggleSkill = (skill: string) => {
    const next = selectedSkills.includes(skill)
      ? selectedSkills.filter((s) => s !== skill)
      : [...selectedSkills, skill];
    setSelectedSkills(next);
    emit(mode, promptOnStart, pickSkills, next);
  };

  const tacticalMode = mode !== "rotation";

  const entitlements = modeEntitlements ?? { tactician: false, strategist: false };

  const modeAllowed = (id: AutoBattleMode) => isAutoBattleModeAllowed(id, entitlements);

  const pickMode = (id: AutoBattleMode) => {
    if (!modeAllowed(id)) {
      onNeedPremium?.();
      return;
    }
    emit(id, promptOnStart, pickSkills, selectedSkills);
  };

  return (
    <div className="bg-kingdom-dark/60 rounded-xl p-3 space-y-3 border border-kingdom-light/30">
      <div className="text-sm font-bold text-white">Auto-battle</div>

      <div className="space-y-1.5">
        {AUTO_BATTLE_MODES.map((m) => {
          const allowed = modeAllowed(m.id);
          return (
          <label
            key={m.id}
            className={clsx(
              "flex items-start gap-2 rounded-lg px-2 py-1.5",
              allowed ? "cursor-pointer" : "opacity-50 cursor-not-allowed",
              mode === m.id ? "bg-kingdom-mid/40" : allowed ? "hover:bg-kingdom-mid/20" : "",
            )}
          >
            <input
              type="radio"
              name="auto-battle-mode"
              checked={mode === m.id}
              disabled={!allowed}
              onChange={() => pickMode(m.id)}
              className="mt-1"
            />
            <span className="flex-1 min-w-0">
              <span className="text-sm text-white font-medium inline-flex items-center gap-1.5">
                {m.name}
                {m.badge && (
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-gray-700 text-gray-300">
                    {m.badge}
                  </span>
                )}
              </span>
              <span className="block text-[11px] text-gray-500">{m.description}</span>
              {!allowed && (
                <span className="block text-[10px] text-amber-400/90 mt-0.5">
                  {m.id === "strategist" ? "Requires Pro" : "Requires VIP or Pro"}
                </span>
              )}
            </span>
          </label>
        );})}
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={promptOnStart}
          onChange={(e) => emit(mode, e.target.checked, pickSkills, selectedSkills)}
        />
        Ask before each battle
      </label>

      {tacticalMode && learnedSkills.length > 0 && (
        <div className="space-y-2 pt-1 border-t border-kingdom-light/20">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={pickSkills}
              onChange={(e) => {
                const next = e.target.checked;
                setPickSkills(next);
                emit(mode, promptOnStart, next, selectedSkills);
              }}
            />
            Limit skills for tactical AI
          </label>
          {pickSkills && (
            <div className="flex flex-wrap gap-1.5">
              {learnedSkills.map((skill) => {
                const on = selectedSkills.includes(skill);
                return (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => toggleSkill(skill)}
                    className={clsx(
                      "text-[10px] font-bold px-2 py-1 rounded-lg border",
                      on
                        ? "border-kingdom-glow text-kingdom-glow bg-kingdom-glow/10"
                        : "border-gray-700 text-gray-500",
                    )}
                  >
                    {skillLabel(skill)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
